import { CircularProgress, Stack, useTheme } from "@mui/material";
import { Camera, FileText, Image, type LucideIcon } from "lucide-react";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { useAddDocument } from "web_myplann/Portfolio/Hooks/useAddDocument";
import {
	type AddDocumentSource,
	AddDocumentSources,
} from "web_myplann/Portfolio/Types/AddDocumentSource";
import { ListRow } from "web_myplann/Shared/Components/ListRow";

type Props = {
	onClose: () => void;
};

export const AddDocumentDrawer: React.FunctionComponent<Props> = ({
	onClose,
}) => {
	const { iconSize, palette, spacingTokens } = useTheme();
	const { t } = useResource();

	const { pendingSource, onPickPhoto, onPickGallery, onPickFile } =
		useAddDocument({ onUploaded: onClose });

	const isUploading = pendingSource !== undefined;

	const options: {
		source: AddDocumentSource;
		icon: LucideIcon;
		label: string;
		onClick: () => void;
	}[] = [
		{
			source: AddDocumentSources.photo,
			icon: Camera,
			label: t(Resources.Portfolio.List.AddDocumentDrawer.Types.photo),
			onClick: onPickPhoto,
		},
		{
			source: AddDocumentSources.gallery,
			icon: Image,
			label: t(Resources.Portfolio.List.AddDocumentDrawer.Types.gallery),
			onClick: onPickGallery,
		},
		{
			source: AddDocumentSources.file,
			icon: FileText,
			label: t(Resources.Portfolio.List.AddDocumentDrawer.Types.file),
			onClick: onPickFile,
		},
	];

	return (
		<Stack pb={spacingTokens.lg}>
			{options.map(({ source, icon: Icon, label, onClick }, index) => (
				<ListRow
					key={source}
					icon={
						pendingSource === source ? (
							<CircularProgress size={iconSize.md} />
						) : (
							<Icon size={iconSize.md} color={palette.primary.main} />
						)
					}
					title={label}
					showChevron={false}
					isLast={index === options.length - 1}
					disabled={isUploading}
					sx={pendingSource === source ? { opacity: 1 } : undefined}
					onClick={onClick}
				/>
			))}
		</Stack>
	);
};
